// features/productBatch/productBatchSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import productBatchService from "../../../services/productBatchService";

const initialState = {
  productBatches: [],
  cumulativeBatches: [],
  productBatch: null,
  isLoading: false,
  error: null,
  success: null,
};

// 🟢 CREATE PRODUCT BATCH
export const createProductBatch = createAsyncThunk(
  "productBatch/createProductBatch",
  async (data, thunkAPI) => {
    try {
      return await productBatchService.createProductBatch(data);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🔵 GET ALL PRODUCT BATCHES
export const getProductBatches = createAsyncThunk(
  "productBatch/getProductBatches",
  async (productId = null, thunkAPI) => {
    try {
      return await productBatchService.getProductBatches(productId);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🟡 GET SINGLE PRODUCT BATCH
export const getSingleProductBatch = createAsyncThunk(
  "productBatch/getSingleProductBatch",
  async (id, thunkAPI) => {
    try {
      return await productBatchService.getSingleProductBatch(id);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🟠 UPDATE PRODUCT BATCH
export const updateProductBatch = createAsyncThunk(
  "productBatch/updateProductBatch",
  async ({ id, data }, thunkAPI) => {
    try {
      return await productBatchService.updateProductBatch(id, data);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🔴 DELETE PRODUCT BATCH
export const deleteProductBatch = createAsyncThunk(
  "productBatch/deleteProductBatch",
  async (id, thunkAPI) => {
    try {
      return await productBatchService.deleteProductBatch(id);
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🟢 GET CUMULATIVE PRODUCT BATCHES WITH STOCK VALUE
export const getCumulativeProductBatches = createAsyncThunk(
  "productBatch/getCumulativeProductBatches",
  async (_, thunkAPI) => {
    try {
      return await productBatchService.getCumulativeProductBatches();
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 🟣 SLICE
const productBatchSlice = createSlice({
  name: "productBatch",
  initialState,
  reducers: {
    resetProductBatchState: (state) => {
      state.isLoading = false;
      state.error = null;
      state.success = null;
      state.productBatch = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // 🟢 CREATE
      .addCase(createProductBatch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createProductBatch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.productBatches.push(action.payload.data || action.payload);
        toast.success("Product batch created successfully");
      })
      .addCase(createProductBatch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // 🔵 GET ALL
      .addCase(getProductBatches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProductBatches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productBatches = action.payload.data || action.payload;
      })
      .addCase(getProductBatches.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // 🟡 GET SINGLE
      .addCase(getSingleProductBatch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSingleProductBatch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.productBatch = action.payload;
      })
      .addCase(getSingleProductBatch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // 🟠 UPDATE
      .addCase(updateProductBatch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateProductBatch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;

        state.productBatches = state.productBatches.map((batch) =>
          batch._id === (action.payload.data?._id || action.payload._id)
            ? (action.payload.data || action.payload)
            : batch
        );

        toast.success("Product batch updated successfully");
      })
      .addCase(updateProductBatch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // 🔴 DELETE
      .addCase(deleteProductBatch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteProductBatch.fulfilled, (state, action) => {
        state.isLoading = false;

        state.productBatches = state.productBatches.filter(
          (batch) => batch._id !== action.payload._id
        );

        toast.success("Product batch deleted successfully");
      })
      .addCase(deleteProductBatch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // 🟢 GET CUMULATIVE PRODUCT BATCHES WITH STOCK VALUE
      .addCase(getCumulativeProductBatches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCumulativeProductBatches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cumulativeBatches = action.payload;
      })
      .addCase(getCumulativeProductBatches.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetProductBatchState } = productBatchSlice.actions;

// 🔎 SELECTORS
export const selectProductBatches = (state) => state.productBatch.productBatches;
export const selectProductBatch = (state) => state.productBatch.productBatch;

export default productBatchSlice.reducer;